// Condition / defect field metadata.

export const CONDITION_FIELDS = [
  {
    key: "genetics",
    label: "Genetics",
    placeholder: "DM carrier, hip-elbow clear, CEA…",
    color: "#16A34A", // green
    description: "Genetic test results, carrier status, DNA results.",
  },
  {
    key: "medical_conditions",
    label: "Medical conditions",
    placeholder: "cleft palate, seizures, allergies…",
    color: "#EC4899", // pink
    description: "Diagnosed or observed medical conditions.",
  },
  {
    key: "standard_compliance",
    label: "Standard compliance",
    placeholder: "oversize 32kg, missing P3, light eye…",
    color: "#9333EA", // purple
    description: "Deviations from the breed standard (size, dentition, colour…)",
  },
];

export const CONDITION_COLOR = CONDITION_FIELDS.reduce(
  (acc, f) => ((acc[f.key] = f.color), acc),
  {},
);

// Split a free-text condition string into a normalised, deduped list of
// short tokens. Splits on commas / newlines / semicolons; trims, lowercases.
export const parseConditions = (text) => {
  if (!text) return [];
  const seen = new Set();
  return text
    .split(/[,\n;]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => s.toLowerCase())
    .filter((s) => {
      if (seen.has(s)) return false;
      seen.add(s);
      return true;
    });
};

// Aggregate conditions across a pedigree's ancestors. Returns:
//   { genetics: { 'cleft palate': [{ idx, name }, ...] }, medical_conditions: {...}, standard_compliance: {...} }
export const collectConditionsByField = (ancestors) => {
  const out = {
    genetics: {},
    medical_conditions: {},
    standard_compliance: {},
  };
  for (const [idxStr, a] of Object.entries(ancestors || {})) {
    if (!a) continue;
    const idx = parseInt(idxStr, 10);
    for (const f of CONDITION_FIELDS) {
      const list = parseConditions(a[f.key] || "");
      for (const item of list) {
        if (!out[f.key][item]) out[f.key][item] = [];
        out[f.key][item].push({ idx, name: a.name || "(unnamed)" });
      }
    }
  }
  return out;
};

// Compute conditions appearing in BOTH pedigrees (sire & dam) — doubled-up.
export const findDoubledConditions = (sirePed, damPed) => {
  const sire = collectConditionsByField(sirePed?.ancestors || {});
  const dam = collectConditionsByField(damPed?.ancestors || {});
  const result = { genetics: [], medical_conditions: [], standard_compliance: [] };
  for (const f of CONDITION_FIELDS) {
    for (const cond of Object.keys(sire[f.key])) {
      if (dam[f.key][cond]) {
        result[f.key].push({
          condition: cond,
          sireOccurrences: sire[f.key][cond],
          damOccurrences: dam[f.key][cond],
        });
      }
    }
  }
  return result;
};

// Helper: does an ancestor have *any* entry for the given field key?
export const ancestorHasField = (ancestor, key) =>
  Boolean(ancestor && (ancestor[key] || "").trim());

// Produce a short, human-readable label for a free-text condition entry.
// Strategy: drop anything after a `:` or `|` (status text), then pick the
// longest word from what remains. Truncate to 14 chars with an ellipsis.
//   "Alanine Aminotransferase Activity : Affected" -> "Aminotransfera…"
//   "DM" -> "DM"
//   "hip dysplasia grade III" -> "dysplasia"
export const summarizeCondition = (token) => {
  if (!token) return "";
  const head = String(token).split(/[:|]/)[0].trim();
  const words = head.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  let longest = "";
  for (const w of words) if (w.length > longest.length) longest = w;
  if (!longest) longest = head;
  return longest.length > 14 ? longest.slice(0, 14) + "…" : longest;
};

/**
 * Walk a pedigree's ancestors and return a Map keyed by `${fieldKey}|${token}`
 * with `{fieldKey, color, token, label, count, indices}` for each token whose
 * **distinct ancestor count is >= 2** — i.e. it shows up repeatedly across
 * the tree. Used by PedigreeTree/PedigreeNode to highlight trends.
 */
export const buildRepeatedConditionMap = (ancestors) => {
  const counts = new Map();
  for (const [idxStr, a] of Object.entries(ancestors || {})) {
    if (!a) continue;
    const idx = parseInt(idxStr, 10);
    for (const f of CONDITION_FIELDS) {
      const tokens = parseConditions(a[f.key] || "");
      for (const t of tokens) {
        const key = `${f.key}|${t}`;
        const cur = counts.get(key) || {
          fieldKey: f.key,
          color: f.color,
          token: t,
          label: summarizeCondition(t),
          count: 0,
          indices: new Set(),
        };
        cur.indices.add(idx);
        cur.count = cur.indices.size;
        counts.set(key, cur);
      }
    }
  }
  const repeats = new Map();
  for (const [k, v] of counts.entries()) {
    if (v.count >= 2) repeats.set(k, v);
  }
  return repeats;
};

/**
 * For a given ancestor, return the list of repeated-condition chips it
 * carries (only tokens present in `repeatedMap`).
 */
export const repeatedChipsForAncestor = (ancestor, repeatedMap) => {
  if (!ancestor || !repeatedMap || repeatedMap.size === 0) return [];
  const out = [];
  for (const f of CONDITION_FIELDS) {
    const tokens = parseConditions(ancestor[f.key] || "");
    for (const t of tokens) {
      const key = `${f.key}|${t}`;
      const r = repeatedMap.get(key);
      if (r) out.push({ ...r, originalText: t });
    }
  }
  return out;
};

/**
 * Per-generation density of each condition field. Returns:
 *   [{ field, label, color, cells: [{ gen, count, slots, items }] }, ...]
 */
export const buildConditionsDensity = (ancestors, totalGenerations) => {
  const out = [];
  for (const f of CONDITION_FIELDS) {
    const cells = [];
    for (let g = 0; g < totalGenerations; g++) {
      const startIdx = Math.pow(2, g) - 1;
      const endIdx = Math.pow(2, g + 1) - 1;
      const items = [];
      let count = 0;
      for (let i = startIdx; i < endIdx; i++) {
        const a = ancestors[String(i)] || ancestors[i];
        if (ancestorHasField(a, f.key)) {
          count += 1;
          items.push({ idx: i, name: a.name || "(unnamed)", value: a[f.key] });
        }
      }
      cells.push({ gen: g, count, slots: endIdx - startIdx, items });
    }
    out.push({ field: f.key, label: f.label, color: f.color, cells });
  }
  return out;
};

/**
 * Aggregate condition tokens across an animal's descendants.
 *
 * `descendants` is the array returned by `/api/animals/{id}/descendants` —
 * each entry has the usual animal fields plus a `depth` (1 = direct child,
 * 2 = grandchild, ...).
 *
 * Returns an array (one entry per CONDITION_FIELD) ordered by `CONDITION_FIELDS`:
 *   [{
 *     field, label, color,
 *     totalProgeny,                // descendants count examined
 *     uniqueAffected,              // number of descendants with ≥1 token in this field
 *     tokens: [                    // sorted by count desc
 *       {
 *         token,                   // normalised lowercase string
 *         label,                   // short summarized label
 *         count,                   // distinct progeny carrying it
 *         carriers: [              // each affected progeny
 *           { id, name, depth, originalText }
 *         ]
 *       }, ...
 *     ]
 *   }, ...]
 *
 * Only tokens with `count >= minRepeats` (default 1) are returned. The
 * caller (UI) decides whether to highlight repeating-only items.
 */
export const buildProgenyConditionsSummary = (descendants, { minRepeats = 1 } = {}) => {
  const result = [];
  const list = Array.isArray(descendants) ? descendants : [];
  for (const f of CONDITION_FIELDS) {
    const tokenMap = new Map(); // token -> { count, carriers[] }
    const carriersForField = new Set();
    for (const d of list) {
      const tokens = parseConditions(d[f.key] || "");
      // Also remember the original text fragment for tooltip context.
      const original = d[f.key] || "";
      for (const t of tokens) {
        let entry = tokenMap.get(t);
        if (!entry) {
          entry = {
            token: t,
            label: summarizeCondition(t),
            count: 0,
            carriers: [],
            seenIds: new Set(),
          };
          tokenMap.set(t, entry);
        }
        if (!entry.seenIds.has(d.id)) {
          entry.seenIds.add(d.id);
          entry.count += 1;
          entry.carriers.push({
            id: d.id,
            name: d.name || "(unnamed)",
            depth: d.depth || 1,
            originalText: original,
          });
        }
      }
      if (tokens.length > 0) carriersForField.add(d.id);
    }
    const tokens = Array.from(tokenMap.values())
      .filter((t) => t.count >= minRepeats)
      .map(({ seenIds, ...rest }) => ({
        ...rest,
        // Sort each token's carriers by depth (closest progeny first), then name.
        carriers: rest.carriers
          .slice()
          .sort((a, b) => a.depth - b.depth || a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => b.count - a.count || a.token.localeCompare(b.token));
    result.push({
      field: f.key,
      label: f.label,
      color: f.color,
      totalProgeny: list.length,
      uniqueAffected: carriersForField.size,
      tokens,
    });
  }
  return result;
};
